import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type CareStaffSetup, type OnboardingMe, type ShiftOverview } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useDemoBuilding } from '../../session/DemoScope';
import { useReadOnly } from '../../ui/ReadOnly';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import type { QueryState } from '../../useQuery';
import './careShift.css';
import { useBuildLabels } from '../../ui/buildLabels';

// Ports of the Console design's Care Staff pieces kept in V4 (in "Aithora ConsoleV4.dc.html"):
// the setup line (csSu*, T6118-6167), the hall board (csRoster, T6285-6311) and Fluids today
// (csFluids, T6312-6326, under the hall board in V4). Class names map to the design's
// inline styles in careShift.css. The V4 Next up list and To do are CareStaffOverview.tsx
// and careTodo.tsx.

const pad = (n: number) => String(n).padStart(2, '0');
const hhmm = (t: number) => {
  const d = new Date(t);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const pct = (a: number, b: number) => (b ? Math.min(100, Math.round((a / b) * 100)) : 0);

// ── Setup line (design csSu*) ───────────────────────────────────────────────

const SETUP_HIDE_KEY = 'aithora_cs_setup_hidden';

/**
 * "Get set up for your shift · n of 3". Hidden while an emergency is unclaimed (the server
 * says show:false) and once hidden by the person. Start opens the three steps in place.
 * Step 1 pages this phone (POST /notify/test-push, confirm-first) and reads the test until
 * it is tapped; steps 2 and 3 are marked by the person (PUT /onboarding/me). The practice
 * row and the Atlas draft are practice: nothing reaches a chart.
 */
export function CareSetup({ q }: { q: QueryState<OnboardingMe> }) {
  const { client, session } = useSession();
  const readOnly = useReadOnly();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try {
      return window.sessionStorage.getItem(SETUP_HIDE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [asking, setAsking] = useState(false);
  const [test, setTest] = useState<{ id: string; ack: number | null } | null>(null);
  const [practice, setPractice] = useState<'done' | 'snoozed' | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [atlas, setAtlas] = useState<'signed' | 'discarded' | null>(null);
  const [polls, setPolls] = useState(0);
  const { reload } = q;
  useEffect(() => {
    if (!test || test.ack !== null || polls > 120) return;
    const t = setTimeout(async () => {
      try {
        const st = await api.testStatus(client, { params: { id: test.id } });
        if (st.ackSeconds !== null) {
          setTest({ id: test.id, ack: st.ackSeconds });
          reload();
          return;
        }
      } catch {
        /* the next read may answer */
      }
      setPolls((n) => n + 1);
    }, 1000);
    return () => clearTimeout(t);
  }, [test, polls, client, reload]);

  const d = q.data;
  if (hidden || !d || d.kind !== 'care_staff' || !('steps' in d) || !d.show) return null;
  const s = d as CareStaffSetup;
  const step = <K extends CareStaffSetup['steps'][number]['key']>(k: K) => s.steps.find((x) => x.key === k);
  const push = step('push') as Extract<CareStaffSetup['steps'][number], { key: 'push' }> | undefined;
  const pushAck = test?.ack ?? (push?.done ? push.pushAckSeconds : null);
  const pushDone = !!push?.done || (test !== null && test.ack !== null);
  const practDone = !!step('practice')?.done || practice !== null;
  const atlasDone = !!step('atlas')?.done || atlas !== null;
  const doneN = [pushDone, practDone, atlasDone].filter(Boolean).length;
  const complete = doneN === 3;
  const nextIdx = !pushDone ? 0 : !practDone ? 1 : !atlasDone ? 2 : -1;
  const isOpen = open && !complete;
  const hide = (persist: boolean) => {
    try {
      (persist ? window.localStorage : window.sessionStorage).setItem(SETUP_HIDE_KEY, '1');
    } catch {
      /* storage off: it shows again next time */
    }
    setHidden(true);
  };
  const mark = async (body: { practice?: boolean; atlas?: boolean }) => {
    await api.onboardingMeSave(client, { body });
  };
  const num = (done: boolean, i: number) => `csu2__num${done ? ' csu2__num--done' : i === nextIdx ? ' csu2__num--next' : ''}`;

  return (
    <div className="csu2" data-testid="care-setup" data-screen-label="Care Staff setup">
      <div className="csu2__head">
        <span className="csu2__title">{complete ? 'You’re set' : 'Get set up for your shift'}</span>
        <span className="csu2__progress">{complete ? `Alerts reach this phone in ${pushAck ?? '–'}s` : `${doneN} of 3`}</span>
        <span className="csu2__time">{complete ? '' : `about ${3 - doneN} min`}</span>
        <span className="csu2__grow" />
        <span className="csu2__acts">
          <button
            type="button"
            className={`csu2__toggle${isOpen ? ' csu2__toggle--open' : complete ? ' csu2__toggle--done' : ''}`}
            onClick={() => (complete ? hide(true) : setOpen(!open))}
          >
            {complete ? 'Done' : isOpen ? 'Collapse' : doneN ? 'Continue' : 'Start'}
          </button>
          {!complete && (
            <button type="button" className="csu2__later" onClick={() => hide(false)}>
              Not now
            </button>
          )}
        </span>
      </div>
      <div className="csu2__track">
        <div className={`csu2__bar${complete ? ' csu2__bar--done' : ''}`} style={{ width: `${Math.round((doneN / 3) * 100)}%` }} />
      </div>
      {isOpen && (
        <div className="csu2__steps">
          <div className="csu2__step">
            <span className="csu2__sh">
              <span className={num(pushDone, 0)}>{pushDone ? '✓' : '1'}</span>
              <span className="csu2__st">Confirm alerts reach this phone</span>
            </span>
            <span className="csu2__why">We send one test alert. Tap it on your phone and we time how fast it got to you.</span>
            <span className="csu2__foot">
              {!test && !pushDone && (
                <button type="button" className={`csu2__act${nextIdx === 0 ? ' csu2__act--primary' : ''}`} disabled={readOnly} onClick={() => setAsking(true)}>
                  Send test alert
                </button>
              )}
              <span className={`csu2__ev${pushDone ? ' csu2__ev--done' : ''}`}>{test ? (test.ack !== null ? `Reached you and tapped in ${test.ack}s` : 'Sent. Tap the alert on your phone…') : pushDone ? `Reached you and tapped in ${pushAck ?? '–'}s` : 'Not tested yet'}</span>
            </span>
          </div>
          <div className="csu2__step">
            <span className="csu2__sh">
              <span className={num(practDone, 1)}>{practDone ? '✓' : '2'}</span>
              <span className="csu2__st">Try the two buttons</span>
            </span>
            <span className="csu2__why">Every row works the same way: Done when it{'’'}s done, Snooze when it can{'’'}t happen yet. This practice row is never saved.</span>
            {!practDone && (
              <span className="csu2__practice">
                <span className="csu2__pk">PRACTICE</span>
                <span className="csu2__pr">Rose Test {'·'} Rm 000 {'·'} Water round</span>
                <button
                  type="button"
                  className="csu2__done"
                  disabled={readOnly}
                  onClick={async () => {
                    await mark({ practice: true });
                    setPractice('done');
                  }}
                >
                  Done
                </button>
                <button
                  type="button"
                  className="csu2__snooze"
                  disabled={readOnly}
                  onClick={async () => {
                    await mark({ practice: true });
                    setPractice('snoozed');
                  }}
                >
                  Snooze {'▾'}
                </button>
              </span>
            )}
            <span className="csu2__foot">
              <span className={`csu2__ev${practDone ? ' csu2__ev--done' : ''}`}>
                {practDone
                  ? practice === 'snoozed'
                    ? 'Snoozed. On a real row you pick 15 min, 1 hr or end of shift. Nothing was saved.'
                    : 'Marked done. Real rows work the same way. Nothing was saved.'
                  : ''}
              </span>
            </span>
          </div>
          <div className="csu2__step">
            <span className="csu2__sh">
              <span className={num(atlasDone, 2)}>{atlasDone ? '✓' : '3'}</span>
              <span className="csu2__st">Say one note to Atlas</span>
            </span>
            <span className="csu2__why">Say to your device: {'“'}Hey Siri, tell Atlas Rose Test drank a full cup of water.{'”'} Atlas drafts it; you sign.</span>
            {said && !atlasDone && (
              <span className="csu2__draft">
                <span className="csu2__dk">ATLAS DRAFT {'·'} PRACTICE</span>
                <span className="csu2__dt">{`Rose Test drank a full cup of water at ${said}. Intake logged: 240 ml.`}</span>
                <span className="csu2__dacts">
                  <button
                    type="button"
                    className="csu2__sign"
                    disabled={readOnly}
                    onClick={async () => {
                      await mark({ atlas: true });
                      setAtlas('signed');
                    }}
                  >
                    Sign
                  </button>
                  <button
                    type="button"
                    className="csu2__snooze"
                    disabled={readOnly}
                    onClick={async () => {
                      await mark({ atlas: true });
                      setAtlas('discarded');
                    }}
                  >
                    Discard
                  </button>
                </span>
              </span>
            )}
            <span className="csu2__foot">
              {!atlasDone && !said && (
                <button type="button" className={`csu2__act${nextIdx === 2 ? ' csu2__act--primary' : ''}`} onClick={() => setSaid(hhmm(Date.now()))}>
                  I said it
                </button>
              )}
              <span className={`csu2__ev${atlasDone ? ' csu2__ev--done' : ''}`}>
                {atlasDone
                  ? atlas === 'discarded'
                    ? 'Discarded. Nothing is kept until you sign.'
                    : 'Signed. Real notes go to the resident’s chart and the Truth Ledger; this one didn’t.'
                  : said
                    ? ''
                    : 'Assistant off? Turn it on in your phone’s Settings, or type to Atlas instead.'}
              </span>
            </span>
          </div>
        </div>
      )}
      {asking && (
        <ConfirmDialog
          title="Send a test alert to this phone?"
          body="One test alert goes to you only. Tap it when it arrives and we time how fast it reached you."
          confirmLabel="Send test alert"
          onConfirm={async () => {
            const r = await api.testPush(client, { body: withConfirm({ user_id: session?.user.id }) });
            setPolls(0);
            setTest({ id: r.testId, ack: null });
          }}
          onClose={() => setAsking(false)}
        />
      )}
    </div>
  );
}

// ── Hall board (design csRoster) ───────────────────────────────────────────

const STATUS_DOT = { alert: '#B3261E', attention: '#5B6B7C', stable: '#2F8A6F' } as const;
const RED_FLAGS = new Set(['FALL', 'DNR', 'ISO']);

export function HallBoard({ s, now, sel, onSel }: { s: ShiftOverview; now: number; sel: string | null; onSel: (room: string | null) => void }) {
  const demoBuilding = useDemoBuilding();
  const hall = s.hall ?? [];
  const alerts = hall.filter((r) => r.status === 'alert').length;
  const attention = hall.filter((r) => r.status === 'attention').length;
  // In a seeded building scope (no banner, no "demo" on screen): no SAMPLE word here.
  const sample = !demoBuilding && hall.some((r) => r.hrBasis === 'sample') ? ' SAMPLE' : '';
  return (
    <div className="csx-min">
      <span className="csx-meta">{`${hall.length} assigned · ${alerts} alert · ${attention} need attention · tap a room to filter the worklist · HR trend last 24h${sample}`}</span>
      <span className="csh2">
        {hall.map((r) => {
          const mine = s.tasks.filter((w) => w.name === r.name).sort((a, b) => Date.parse(a.due) - Date.parse(b.due));
          const nxt = mine[0];
          const st = r.status === 'alert' ? 'alert' : nxt && Date.parse(nxt.due) < now ? 'attention' : r.status;
          const done = s.done.filter((p) => p.name === r.name).length;
          const total = done + mine.length;
          const hr = r.hr;
          const mn = Math.min(...hr) - 4;
          const mx = Math.max(...hr) + 4;
          const spark = hr.map((v, i) => `${(i * (60 / (hr.length - 1))).toFixed(1)},${(18 - ((v - mn) / (mx - mn)) * 18).toFixed(1)}`).join(' ');
          const rising = hr.length > 1 && hr[hr.length - 1] - hr[0] >= 12;
          const isSel = sel === r.room;
          return (
            <span
              key={r.residentId}
              role="button"
              tabIndex={0}
              className={`csh2__card csh2__card--${isSel ? 'sel' : st}`}
              onClick={() => onSel(isSel ? null : r.room)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSel(isSel ? null : r.room);
                }
              }}
            >
              <span className="csh2__top">
                <span className="csh2__room">{r.room ?? '–'}</span>
                <span className="csh2__name">{r.name}</span>
                <Link to="/residents" title="Open resident record" className="csh2__chart" onClick={(e) => e.stopPropagation()}>
                  Chart {'→'}
                </Link>
                <span className="csh2__dot" style={{ background: STATUS_DOT[st] }} />
              </span>
              <span className="csh2__flags">
                {r.flags.map((f) => (
                  <span key={f} className={`csh2__flag${RED_FLAGS.has(f) ? ' csh2__flag--red' : ''}`}>
                    {f}
                  </span>
                ))}
              </span>
              <span className={`csh2__now csh2__now--${st}`}>{r.status === 'alert' && r.alertLine ? r.alertLine : nxt ? `next ${hhmm(Date.parse(nxt.due))} · ${nxt.what}` : 'nothing left this shift'}</span>
              <span className="csh2__foot">
                <svg viewBox="0 0 60 18" preserveAspectRatio="none" className="csh2__spark" aria-hidden="true">
                  <polyline points={spark} fill="none" stroke={rising ? '#B3261E' : '#54606C'} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                </svg>
                <span className="csh2__hr">{`HR ${hr[hr.length - 1] ?? '–'}${rising ? ' ↑' : ''}`}</span>
                <span className="csh2__track">
                  <span className={`csh2__bar${done === total ? ' csh2__bar--done' : ''}`} style={{ width: `${pct(done, total)}%` }} />
                </span>
                <span className="csh2__prog">{`${done}/${total}`}</span>
              </span>
            </span>
          );
        })}
      </span>
    </div>
  );
}

// ── Fluids (design csFluids; V4 draws it under the hall board) ──

/** The residents the fluids tab lists: behind target by more than a cup and a half, or at fall risk (design csFluids). */
export function fluidsShown(s: ShiftOverview, added: Record<string, number>) {
  return (s.fluids ?? []).filter((r) => r.targetMl - r.mlToday - (added[r.residentId] ?? 0) > 200 || r.fallRisk).slice(0, 5);
}
export function FluidsList({ s, added, onAdd }: { s: ShiftOverview; added: Record<string, number>; onAdd: (residentId: string) => void }) {
  const build = useBuildLabels();
  const readOnly = useReadOnly();
  const demoBuilding = useDemoBuilding();
  return (
    <div className="csx-narrow">
      <div className="csx-min">
        <div className="csx-head">
          <span className="csx-title">Fluids today</span>
          <span className="csx-hmeta">{`intake vs daily target · amber under 60% · +120 mL logs a cup${s.fluidsReal === false && !demoBuilding && build ? ' · SAMPLE' : ''}`}</span>
        </div>
        {fluidsShown(s, added).map((r) => {
          const have = r.mlToday + (added[r.residentId] ?? 0);
          const p = pct(have, r.targetMl);
          const behind = p < 60;
          return (
            <span key={r.residentId} className="csf">
              <Link to="/residents" title="Open resident record" className="csf__name">
                {r.name} <span className="csf__room">{r.room ?? ''}</span>
              </Link>
              <span className="csf__track">
                <span className={`csf__bar${behind ? ' csf__bar--behind' : ''}`} style={{ width: `${p}%` }} />
                <span className="csf__target" />
              </span>
              <span className={`csf__val${behind ? ' csf__val--behind' : ''}`}>{`${have} / ${r.targetMl} mL`}</span>
              <button type="button" className="csf__add" disabled={readOnly} onClick={() => onAdd(r.residentId)}>
                +120
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
